import { useEffect, useMemo } from 'react'

import { F0Select, F0Text } from '@factorialco/f0-react'
import { Stack } from '@factorialco/f0-react/dist/experimental'
import {
  TicketingCategoriesForSelectorDocument,
  type TicketingCategoriesForSelectorQuery,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

export interface Category {
  id: string
  name: string
  description: string | null
  priorityMode: string
}

interface CategorySelectorProps {
  value: Category | null
  onSelect: (category: Category | null) => void
  label?: string
  required?: boolean
  disabled?: boolean
  error?: boolean
}

export const CategorySelector = ({
  value,
  onSelect,
  label,
  required = false,
  disabled = false,
  error = false,
}: CategorySelectorProps) => {
  const { data, loading } = useQuery<TicketingCategoriesForSelectorQuery, Record<string, never>>(
    TicketingCategoriesForSelectorDocument
  )

  const categories = useMemo(() => data?.ticketing?.ticketCategories ?? [], [data])

  const options = useMemo(
    () =>
      categories.map((c) => ({
        value: String(c.id),
        label: c.name,
        description: c.description ?? undefined,
      })),
    [categories]
  )

  // When the query resolves and a value is already selected (e.g. prefilled from One),
  // sync the full category object (including priorityMode) back to the parent.
  // Without this, priorityMode stays as the empty-string placeholder set at init time.
  useEffect(() => {
    if (!value?.id || !categories.length) return
    const cat = categories.find((c) => String(c.id) === value.id)
    if (cat && cat.priorityMode !== value.priorityMode) {
      onSelect({
        id: String(cat.id),
        name: cat.name,
        description: cat.description ?? null,
        priorityMode: cat.priorityMode,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, value?.id])

  const handleChange = (categoryId: string) => {
    const cat = categories.find((c) => String(c.id) === categoryId)
    onSelect(
      cat
        ? {
            id: String(cat.id),
            name: cat.name,
            description: cat.description ?? null,
            priorityMode: cat.priorityMode,
          }
        : null
    )
  }

  if (!loading && categories.length === 0) {
    return (
      <Stack gap="1">
        <F0Text
          variant="description"
          content={label ?? i18n.t('ticketing.inbox.create.category')}
        />
        <F0Text variant="description" content={i18n.t('ticketing.inbox.create.no_categories')} />
      </Stack>
    )
  }

  return (
    <F0Select
      label={label ?? i18n.t('ticketing.inbox.create.category')}
      value={value?.id ?? ''}
      onChange={handleChange}
      options={options}
      placeholder={i18n.t('ticketing.inbox.create.category_placeholder')}
      required={required}
      disabled={disabled}
      error={error}
      searchable
      showSearchBox
    />
  )
}
